import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useCan } from '../../shell/Can.js';
import { DataGrid } from '../../components/DataGrid.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import {
  listWorkOrders,
  cancelWorkOrder,
  submitWorkOrder,
  type WorkOrderSummary,
} from '../../api/brandingWorkOrderApi.js';
import {
  useWorkOrderBudget,
  WorkOrderBudgetWarning,
  workOrderSubmittedMessage,
} from './WorkOrderBudgetWarning.js';
import {
  listPlanningCycles,
  currentCycleId,
  type PlanningCycle,
} from '../../api/vendorBrandingProfileApi.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/**
 * Where an order has stopped, in one line.
 *
 * An order that is finished, cancelled or still a draft is not waiting on
 * anybody, and saying "—" is more honest than inventing a level for it.
 */
export function whereItIsStuck(w: WorkOrderSummary): string {
  if (w.pendingLevel == null) return '\u2014';
  const who = w.pendingWith ?? w.pendingRoleCode;
  const level = w.totalLevels && w.totalLevels > 1
    ? `Level ${w.pendingLevel} of ${w.totalLevels}`
    : `Level ${w.pendingLevel}`;
  return who ? `${level} \u00b7 ${who}` : level;
}

/**
 * Work orders issued to vendors. A vendor sees only its own, because the API
 * scopes every read to the caller's tenant and the vendor role's menu grant
 * decides who reaches this screen at all.
 */
export function BrandingWorkOrderListPage(): ReactNode {
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const { day, dayRange } = useBusinessDay();
  const [rows, setRows] = useState<readonly WorkOrderSummary[] | null>(null);
  const [cycles, setCycles] = useState<readonly PlanningCycle[]>([]);
  const [cycleId, setCycleId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const budget = useWorkOrderBudget();
  // The same grant the detail page reads: sending a draft on edits that order.
  const maySubmit = useCan('/admin/work-orders', 'edit');

  async function submitDraft(w: WorkOrderSummary): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    budget.clear();
    try {
      const submitted = await submitWorkOrder(w.workOrderId, token);
      budget.record(submitted.budgetStatus);
      setNotice(workOrderSubmittedMessage(w.woNumber));
      const r = await listWorkOrders(token, undefined, cycleId || undefined);
      setRows(r.data);
    } catch (e) {
      budget.recordFailure(e);
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function confirmCancel(w: WorkOrderSummary): Promise<void> {
    if (!reason.trim()) {
      setError('Say why this order is being cancelled.');
      return;
    }
    setBusy(true);
    setError(null);
    budget.clear();
    try {
      await cancelWorkOrder(w.workOrderId, reason.trim(), token);
      setNotice(`${w.woNumber} is cancelled.`);
      setCancelling(null);
      setReason('');
      const r = await listWorkOrders(token, undefined, cycleId || undefined);
      setRows(r.data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    let alive = true;
    listPlanningCycles(token)
      .then((c) => { if (alive) { setCycles(c); setCycleId((p) => p || currentCycleId(c)); } })
      .catch((e) => { if (alive) setError(`Financial years could not be loaded: ${(e as Error).message}`); });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    let alive = true;
    listWorkOrders(token, undefined, cycleId || undefined)
      .then((r) => { if (alive) setRows(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, cycleId]);

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>Loading…</p>;

  return (
    <main aria-label="work-order-list">
      <h1>Work orders</h1>
      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      <WorkOrderBudgetWarning warning={budget.warning} />

      <label htmlFor="wol-cycle">Financial year</label>
      <select
        id="wol-cycle"
        value={cycleId}
        onChange={(e) => setCycleId(e.target.value)}
        data-testid="filter-cycle"
      >
        <option value="">All years</option>
        {cycles.map((c) => (
          <option key={c.planningCycleId} value={c.planningCycleId}>{c.cycleName}</option>
        ))}
      </select>

      <p><Link to="/admin/work-orders/new" data-testid="new-work-order">Issue work order</Link></p>

      <DataGrid<WorkOrderSummary>
        caption="work-orders-table"
        rows={rows}
        getRowId={(w) => w.workOrderId}
        rowTestId={(w) => `row-${w.workOrderId}`}
        searchPlaceholder="Search work orders…"
        exportFileName="work-orders.csv"
        columns={[
          { key: 'woNumber', header: 'Number', render: (w) => <Link to={`/admin/work-orders/${w.workOrderId}`}>{w.woNumber}</Link>, value: (w) => w.woNumber },
          { key: 'vendor', header: 'Vendor', value: (w) => `${w.vendorCode} — ${w.vendorName}` },
          { key: 'cycleCode', header: 'FY', value: (w) => w.cycleCode },
          {
            // `value` feeds sort, filter, search and the CSV export, so it
            // stays the stored day: ISO dates sort chronologically under
            // localeCompare, and a spreadsheet reads them unambiguously.
            // `render` is what a person sees.
            key: 'validity',
            header: 'Validity',
            value: (w) => `${day(w.validFromDate)} → ${day(w.validToDate)}`,
            render: (w) => dayRange(w.validFromDate, w.validToDate),
          },
          { key: 'woStatusCode', header: 'Status', value: (w) => w.woStatusCode },
          {
            key: 'stuckAt',
            header: 'Waiting on',
            // "PENDING_APPROVAL" says the order is waiting; it does not say
            // what for. The level it is sitting at and the person holding it
            // are what anybody chasing the order actually asks.
            value: (w) => whereItIsStuck(w),
          },
          { key: 'totalAmount', header: 'Amount', value: (w) => w.totalAmount, render: (w) => money(Number(w.totalAmount)) },
          {
            key: 'action',
            header: 'Action',
            render: (w) =>
              w.woStatusCode === 'CANCELLED' || w.woStatusCode === 'ARCHIVED' ? null : cancelling === w.workOrderId ? (
                <span>
                  <label htmlFor={`why-${w.workOrderId}`}>Why</label>
                  <input
                    id={`why-${w.workOrderId}`}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason"
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void confirmCancel(w)}
                    data-testid={`confirm-cancel-${w.workOrderId}`}
                  >
                    Confirm cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => { setCancelling(null); setReason(''); }}
                  >
                    Keep it
                  </button>
                </span>
              ) : (
                <span>
                  {w.woStatusCode === 'DRAFT' && maySubmit ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void submitDraft(w)}
                      data-testid={`submit-${w.workOrderId}`}
                    >
                      Submit for approval
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => { setCancelling(w.workOrderId); setReason(''); setNotice(null); }}
                    data-testid={`cancel-${w.workOrderId}`}
                  >
                    Cancel order
                  </button>
                </span>
              ),
          },
        ]}
      />
    </main>
  );
}
